import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authApi, type CareTeamRoleType } from '../api/auth';
import { Loading, PageError } from '../components/State';
import { isApiError } from '../lib/apiClient';
import { useSession } from '../lib/session';
import './InvoicesPage.css';
import './BroadcastPage.css';
import './DiscoverPage.css';
import './SettingsPage.css';

export function SettingsPage() {
  const profile = useSession((s) => s.profile);
  const setProfile = useSession((s) => s.setProfile);
  const signOut = useSession((s) => s.signOut);
  const navigate = useNavigate();

  const me = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const roles = useQuery({ queryKey: ['roles'], queryFn: authApi.roles });

  const [form, setForm] = useState({
    name: '',
    organization: '',
    roleType: 'other' as CareTeamRoleType,
    credentials: '',
    phone: '',
    serviceArea: '',
    address: '',
    bio: '',
    isDiscoverable: false,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const p = me.data;
    if (p) {
      setForm({
        name: p.name ?? '',
        organization: p.organization ?? '',
        roleType: p.roleType ?? 'other',
        credentials: p.credentials ?? '',
        phone: p.phone ?? '',
        serviceArea: p.serviceArea ?? '',
        address: p.address ?? '',
        bio: p.bio ?? '',
        isDiscoverable: p.isDiscoverable ?? false,
      });
    }
  }, [me.data]);

  const save = useMutation({
    mutationFn: () => authApi.updateMe(form),
    onSuccess: (updated) => {
      setProfile(updated);
      me.refetch();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (me.isLoading) return <Loading />;
  if (me.isError) return <PageError error={me.error} onRetry={() => me.refetch()} />;

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="set">
      <h1>Settings</h1>

      <section className="disc__section" style={{ borderTop: 'none', paddingTop: 0 }}>
        <h2>Profile</h2>
        {profile?.email && (
          <div className="field">
            <label>Email</label>
            <input value={profile.email} disabled />
          </div>
        )}
        <div className="inv__two">
          <div className="field">
            <label>Name</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="field">
            <label>Organization</label>
            <input value={form.organization} onChange={(e) => set('organization', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Role</label>
          <select
            value={form.roleType}
            onChange={(e) => set('roleType', e.target.value as CareTeamRoleType)}
          >
            {(roles.data ?? [{ value: form.roleType, label: form.roleType }]).map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="inv__two">
          <div className="field">
            <label>Credentials</label>
            <input value={form.credentials} onChange={(e) => set('credentials', e.target.value)} />
          </div>
          <div className="field">
            <label>Phone</label>
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Service area</label>
          <input value={form.serviceArea} onChange={(e) => set('serviceArea', e.target.value)} />
        </div>
        <div className="field">
          <label>Address</label>
          <input value={form.address} onChange={(e) => set('address', e.target.value)} />
        </div>
        <div className="field">
          <label>Bio</label>
          <textarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} />
        </div>
        <label className="bc__check">
          <input
            type="checkbox"
            checked={form.isDiscoverable}
            onChange={(e) => set('isDiscoverable', e.target.checked)}
          />
          Discoverable — let caregivers find me by service area
        </label>

        {save.isError && (
          <p className="error-text">
            {isApiError(save.error) ? save.error.message : 'Could not save your profile.'}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn" disabled={save.isPending || !form.name.trim()} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save profile'}
          </button>
          {saved && <span className="bc__done">Saved</span>}
        </div>
      </section>

      <ChangePassword />

      <section className="disc__section">
        <h2>Account</h2>
        <button
          className="btn btn--ghost"
          onClick={() => {
            signOut();
            navigate('/login', { replace: true });
          }}
        >
          Log out
        </button>
      </section>
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const m = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = current.length > 0 && next.length >= 6 && next === confirm;

  return (
    <section className="disc__section">
      <h2>Change password</h2>
      <div className="field">
        <label>Current password</label>
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </div>
      <div className="inv__two">
        <div className="field">
          <label>New password</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={6} />
        </div>
        <div className="field">
          <label>Confirm new password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </div>
      </div>
      {mismatch && <p className="error-text">Passwords don’t match.</p>}
      {m.isError && (
        <p className="error-text">
          {isApiError(m.error) ? m.error.message : 'Could not change your password.'}
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn" disabled={!valid || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Updating…' : 'Update password'}
        </button>
        {m.isSuccess && <span className="bc__done">Password updated</span>}
      </div>
    </section>
  );
}
